import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useController } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import i18next from "i18next";
import {
  BUTTON_CONNECT_ACCOUNT,
  FIELD_REQUIRED,
  LINKEDIN_PUBLISH_AS_PERSONAL_LABEL,
  LINKEDIN_PUBLISH_AS_COMPANY_PAGE_LABEL,
} from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startSyncAdsAccountsEntities } from "../../actions/user";
import {
  ADS_INTEGRATION_ENTITIES,
  GOOGLE,
  META,
  TIKTOK,
  LINKEDIN,
  LINKEDIN_PUBLISH_AS,
} from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import AddIcon from "@mui/icons-material/Add";
import useButtonActionPlatform from "../../hooks/useButtonActionPlatform";
import { getUniqueID } from "../../utils/numbers";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const filter = createFilterOptions();
const ID_ADD = "Add Account";

const extractLinkedInOrgIdFromReference = (reference = "") => {
  if (!reference) return "";
  const match = reference.match(/urn:li:organization:(.+)/);
  return match ? match[1] : "";
};

const buildLinkedInOrganizationOptionsFromAdvertisers = ({
  integration,
  advertisers = [],
}) => {
  const groupedAdvertisers = new Map();

  advertisers.forEach((advertiser) => {
    const reference = advertiser.reference || "";
    const pageName = advertiser.pageName || "";
    const key = `${reference}-${pageName}`;

    if (!groupedAdvertisers.has(key)) {
      groupedAdvertisers.set(key, []);
    }
    groupedAdvertisers.get(key).push(advertiser);
  });

  const organizationAccounts = [];

  groupedAdvertisers.forEach((advertiserGroup) => {
    const firstAdvertiser = advertiserGroup[0];
    const reference = firstAdvertiser.reference || "";
    const orgUrn = extractLinkedInOrgIdFromReference(reference);
    const pageName = firstAdvertiser.pageName || "";
    const displayName = pageName || orgUrn;

    organizationAccounts.push({
      id: firstAdvertiser.id,
      name: pageName || orgUrn,
      integrationID: integration.id,
      publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
      orgUrn,
      displayLabel: displayName,
      advertiserIds: advertiserGroup.map((adv) => adv.id),
    });
  });

  return organizationAccounts;
};

const SelectBusinessAccount = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  platform,
  fullWidth,
  helperText,
  onChangeAction = () => {},
  validationChange = async () => true,
  sxTextField = {},
  linkedInForPublishing = false,
  componentsProps = {},
  disableSyncEntities = false,
  disableAutoSelect = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [id] = useState(getUniqueID());
  const [connectingAccount, setConnectingAccount] = useState(false);
  const [isUpdatingEntities, setIsUpdatingEntities] = useState(false);
  const optionsKeyRef = useRef("");

  const {
    field: { value: fieldValue, onChange: onFieldChange },
  } = useController({
    name,
    control,
  });
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const BUTTON_ACTIONS_COMPONENTS = useButtonActionPlatform();

  const { isLoading, integrations } = useSelectorApp((state) => state.user);
  const metaIntegrations = integrations?.facebook;
  const metaIntegrationIdsKey = useMemo(
    () =>
      Object.keys(metaIntegrations || {})
        .sort()
        .join(","),
    [metaIntegrations]
  );
  const tikTokBusinessIntegrations = integrations?.tiktokBusiness;
  const googleIntegrations = integrations?.google;
  const linkedInIntegrations = integrations?.linkedIn;
  const linkedInIntegrationIdsKey = useMemo(
    () =>
      Object.keys(linkedInIntegrations || {})
        .sort()
        .join(","),
    [linkedInIntegrations]
  );

  const onClickConnectAccount = async () => {
    setConnectingAccount(true);
    const element = document.getElementById(id);
    element?.click();
    return;
  };

  useEffect(() => {
    if (platform !== META || !metaIntegrationIdsKey) {
      return;
    }
    const integrationIDs = metaIntegrationIdsKey.split(",").filter(Boolean);
    setIsUpdatingEntities(true);
    Promise.all(
      integrationIDs.map((fbIntegrationID) =>
        dispatch(
          startSyncAdsAccountsEntities({
            integrationID: fbIntegrationID,
            platform: META,
            entity: ADS_INTEGRATION_ENTITIES.AD_ACCOUNTS,
          })
        )
      )
    ).finally(() => {
      setIsUpdatingEntities(false);
    });
  }, [
    platform,
    metaIntegrationIdsKey,
    connectingAccount,
    dispatch,
    disableSyncEntities,
  ]);

  useEffect(() => {
    if (
      platform !== LINKEDIN ||
      !linkedInIntegrationIdsKey ||
      disableSyncEntities
    ) {
      return;
    }
    const integrationIDs = linkedInIntegrationIdsKey.split(",").filter(Boolean);
    setIsUpdatingEntities(true);
    Promise.all(
      integrationIDs.map((linkedInIntegrationID) =>
        dispatch(
          startSyncAdsAccountsEntities({
            integrationID: linkedInIntegrationID,
            platform: LINKEDIN,
            entity: ADS_INTEGRATION_ENTITIES.AD_ACCOUNTS,
          })
        )
      )
    ).finally(() => {
      setIsUpdatingEntities(false);
    });
  }, [
    platform,
    linkedInIntegrationIdsKey,
    connectingAccount,
    dispatch,
    disableSyncEntities,
  ]);

  useEffect(() => {
    if (platform === META) {
      const businessAccount = Object.keys(metaIntegrations || {}).map((key) => {
        const integration = metaIntegrations[key];
        const { businessAccounts, name } = integration;
        const businessAccountsKeys = Object.keys(businessAccounts);
        return businessAccountsKeys.map((key) => ({
          ...businessAccounts[key],
          integrationID: integration.id,
          accountOwner: name,
        }));
      });
      const flatOptions = businessAccount.flat();
      const nextOptionsKey = flatOptions.map((o) => String(o.id)).join("|");
      if (nextOptionsKey !== optionsKeyRef.current) {
        optionsKeyRef.current = nextOptionsKey;
        setOptions(flatOptions);
      }
    }

    if (platform === TIKTOK) {
      const businessAccount = Object.keys(tikTokBusinessIntegrations || {}).map(
        (key) => {
          const integration = tikTokBusinessIntegrations[key];
          const { advertisers } = integration;
          const advertisersKeys = Object.keys(advertisers);
          return advertisersKeys.map((key) => ({
            ...advertisers[key],
            integrationID: integration.id,
          }));
        }
      );
      setOptions(businessAccount.flat());
    }

    if (platform === GOOGLE) {
      const businessAccount = Object.keys(googleIntegrations || {}).map(
        (key) => {
          const integration = googleIntegrations[key];
          const { name, advertisers } = integration;
          const advertisersKeys = Object.keys(advertisers);
          return advertisersKeys.map((key) => ({
            ...advertisers[key],
            integrationID: integration.id,
            accountOwner: name,
          }));
        }
      );
      setOptions(businessAccount.flat());
    }

    if (platform === LINKEDIN && !linkedInForPublishing) {
      const businessAccount = Object.keys(linkedInIntegrations || {}).map(
        (key) => {
          const integration = linkedInIntegrations[key];
          const { advertisers } = integration;
          const advertisersKeys = Object.keys(advertisers || {});
          return advertisersKeys.map((key) => ({
            ...advertisers[key],
            integrationID: integration.id,
          }));
        }
      );
      setOptions(businessAccount.flat());
    }

    if (platform === LINKEDIN && linkedInForPublishing) {
      const publishingAccounts = Object.values(
        linkedInIntegrations || {}
      ).flatMap((integration) => {
        const accounts = [];

        accounts.push({
          id: integration.id,
          name: integration?.name,
          integrationID: integration.id,
          publishAs: LINKEDIN_PUBLISH_AS.PROFILE,
          isPersonal: true,
          displayLabel: integration?.name || "",
        });

        const pages = Object.values(integration?.pages || {});

        if (pages.length > 0) {
          pages.forEach((page) => {
            const orgId = String(page.id);
            const organizationUrn =
              page.organizationUrn || `urn:li:organization:${orgId}`;
            const orgUrn = extractLinkedInOrgIdFromReference(organizationUrn);
            const matchingAdvertisers = Object.values(
              integration?.advertisers || {}
            ).filter(
              (advertiser) =>
                advertiser?.reference === organizationUrn ||
                extractLinkedInOrgIdFromReference(advertiser?.reference) ===
                  orgId
            );

            accounts.push({
              id: orgId,
              name: page.name || orgId,
              integrationID: integration.id,
              publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
              orgUrn,
              organizationUrn,
              displayLabel: page.name || orgId,
              isPage: true,
              pageId: orgId,
              advertiserIds: matchingAdvertisers.map(
                (advertiser) => advertiser.id
              ),
            });
          });
        } else {
          accounts.push(
            ...buildLinkedInOrganizationOptionsFromAdvertisers({
              integration,
              advertisers: Object.values(integration?.advertisers || {}),
            })
          );
        }

        return accounts;
      });
      setOptions(publishingAccounts);
    }
  }, [
    metaIntegrations,
    tikTokBusinessIntegrations,
    googleIntegrations,
    linkedInIntegrations,
    platform,
    connectingAccount,
    linkedInForPublishing,
  ]);

  useEffect(() => {
    if (disableAutoSelect) {
      return;
    }
    if (!fieldValue && options.length > 0) {
      const firstOption = options[0];
      onFieldChange(firstOption.id);
      onChangeAction(firstOption);
    }
    // eslint-disable-next-line
  }, [options, disableAutoSelect]);

  if (isLoading) return null;
  return (
    <>
      {React.cloneElement(BUTTON_ACTIONS_COMPONENTS[platform], {
        id,
        text: BUTTON_CONNECT_ACCOUNT,
        mode: "add",
        icon: <AddIcon />,
        showButtonIcon: true,
        showLogo: false,
        onClick: () => {
          setOpen(true);
        },
        onCallbackOpenPopup: () => {
          setConnectingAccount(false);
        },
        sx: {
          display: "none",
          justifyContent: "flex-start",
          background: "transparent",
          color: "inherit",
          m: 0,
          ml: -1,
          py: 1,
          px: 2,
          fontSize: "1rem",
          fontWeight: 500,
          "&:hover": {
            background: "transparent",
          },
        },
      })}
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            componentsProps={componentsProps}
            loading={connectingAccount}
            hasLabel={Boolean(label)}
            id={name}
            value={options?.length > 0 ? field.value : ""}
            fullWidth={fullWidth}
            disabled={disabled}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={async (_, value) => {
              const isValid = await validationChange(value);
              if (!isValid) {
                return;
              }
              if (value?.id === ID_ADD) {
                onClickConnectAccount();
                return;
              }

              field.onChange(value?.id);
              onChangeAction(value);
            }}
            isOptionEqualToValue={(option, value) => {
              if (value?.id) {
                return option?.id === value?.id;
              }
              return option?.id === value;
            }}
            disableClearable={true}
            getOptionLabel={(option) => {
              const linkedInOptionLabel = (opt) =>
                opt?.displayLabel || opt?.name || "";

              if (platform === LINKEDIN) {
                const optionValue = options.find((opt) => opt.id === option);
                if (optionValue) {
                  return linkedInOptionLabel(optionValue);
                }
                if (typeof option === "object" && option !== null) {
                  return linkedInOptionLabel(option);
                }
                return typeof option === "string" ? option : "";
              }

              const optionValue = options.find((opt) => opt.id === option);
              if (optionValue) {
                if (optionValue.displayLabel) {
                  return optionValue.displayLabel;
                }
                return `${optionValue?.name} - ${optionValue?.id}`;
              }
              if (typeof option === "string") {
                return option;
              }
              if (option?.displayLabel) {
                return option.displayLabel;
              }
              return `${option?.name} - ${option?.id}`;
            }}
            options={options}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              filtered.unshift({
                id: ID_ADD,
                name: t(BUTTON_CONNECT_ACCOUNT),
              });

              return filtered;
            }}
            renderOption={(props, option) => {
              const showPublishAsChip =
                linkedInForPublishing &&
                platform === LINKEDIN &&
                option.id !== ID_ADD &&
                (option.publishAs || option.isPersonal || option.isPage);

              const publishAsChipLabel = option.isPersonal
                ? t(LINKEDIN_PUBLISH_AS_PERSONAL_LABEL)
                : option.isPage ||
                    option.publishAs === LINKEDIN_PUBLISH_AS.ORGANIZATION
                  ? t(LINKEDIN_PUBLISH_AS_COMPANY_PAGE_LABEL)
                  : option.publishAs === LINKEDIN_PUBLISH_AS.PROFILE
                    ? t(LINKEDIN_PUBLISH_AS_PERSONAL_LABEL)
                    : null;

              return (
                <Box
                  {...props}
                  component="li"
                  key={`${option.id}-${option.integrationID || option.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {option.id === ID_ADD && (
                      <AddIcon
                        sx={{
                          ml: -0.5,
                          color: "background.paperContrastText",
                        }}
                      />
                    )}
                    <Typography
                      variant="body1"
                      noWrap
                      sx={{ color: "background.paperContrastText" }}
                    >
                      {option.displayLabel || option.name}
                      {option.accountOwner && ` (${option.accountOwner})`}
                    </Typography>
                  </Box>
                  {showPublishAsChip && publishAsChipLabel && (
                    <Chip
                      label={publishAsChipLabel}
                      size="small"
                      variant="outlined"
                      sx={{
                        flexShrink: 0,
                        height: 22,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                hiddenLabel={!label}
                label={label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {connectingAccount ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : isUpdatingEntities ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                helperText={
                  <Box
                    component="span"
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>{!!error ? error?.message : helperText}</span>
                  </Box>
                }
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
              />
            )}
          />
        )}
      />
    </>
  );
};

export default SelectBusinessAccount;
